import { InjectionToken } from '@angular/core'

import { Bugfender, BugfenderClass } from '@bugfender/sdk'

import { CrashHandler } from '@enums/crash-handler'
import { CrashRule } from '@enums/crash-rule'
import { CrashStormVerdict } from '@enums/crash-storm-verdict'
import { classifyWindowError } from '@utils/crash-classifier'
import { BOOTSTRAP_FAILED_TITLE, crashReport, UNHANDLED_REJECTION_TITLE } from '@utils/crash-report'
import { createCrashStormGuard, CrashStormGuard, crashStormKey } from '@utils/crash-storm-guard'
import { PageContext, pageContext } from '@utils/page-context'

/** The part of the Bugfender SDK the crash reporter uses. */
export type CrashReporterSdk = Pick<BugfenderClass, 'sendCrash' | 'warn' | 'forceSendOnce'>

/** Tells which rule, if any, proves a window error is not ours (see `classifyWindowError`). */
export type WindowErrorClassifier = (event: ErrorEvent) => CrashRule | undefined

/** Sends the page's failures as Bugfender crashes: the ones Angular catches and the ones that reach the window. */
export interface CrashReporter {
	/** An error Angular's `ErrorHandler` received. */
	readonly reportAngularError: (error: unknown) => void
	/** Angular failed to start. Skipped when the `ErrorHandler` already reported this same failure. */
	readonly reportFailedStart: (error: unknown) => void
	/** Angular finished starting: errors from now on are reported as `Handler: Angular`. */
	readonly markStarted: () => void
	/** An `error` event that reached the window. */
	readonly reportWindowError: (event: ErrorEvent) => void
	/** An `unhandledrejection` event that reached the window, by its reason. */
	readonly reportRejection: (reason: unknown) => void
}

/** The warning sent instead of the first crash over a storm limit. */
export const CRASH_STORM_WARNING = 'crash-storm'
/** The warning sent just before each masked `Script error.` crash, with what the browser did not hide. */
export const SCRIPT_ERROR_CONTEXT_WARNING = 'script-error-context'
/** The warning sent instead of the crash of a window error a rule proved is not ours, with the full crash text. */
export const THIRD_PARTY_ERROR_WARNING = 'third-party-error'
/** The warning sent instead of the first third-party warning over a storm limit. */
export const THIRD_PARTY_STORM_WARNING = 'third-party-storm'

const MASKED_SCRIPT_ERROR = 'Script error.'

interface Failure {
	readonly title: string
	readonly filename?: string
	readonly line?: number
	readonly error: unknown
	readonly handler: CrashHandler
}

/**
 * The browser hides the message, file, line and error of a failure in a script from another origin loaded without
 * CORS, which leaves only this text. The crash is kept: a session with a crash uploads its logs, so the context
 * warning sent with it arrives too.
 */
const isMaskedScriptError = ({ message, filename, lineno, error }: ErrorEvent): boolean =>
	message === MASKED_SCRIPT_ERROR && !filename && !lineno && error == null

/**
 * The SDK's rule for titles: an Error's message, otherwise a fixed title. zone.js hands a rejected promise's reason to
 * the `ErrorHandler` as is, so a non-Error rejection and a non-Error throw look the same and both get the SDK's
 * rejection title.
 */
const crashTitle = (error: unknown, handler: CrashHandler): string => {
	if (error instanceof Error) return error.message
	if (handler === CrashHandler.Bootstrap) return BOOTSTRAP_FAILED_TITLE

	return UNHANDLED_REJECTION_TITLE
}

/**
 * A failed start reaches two places: Angular hands it to its `ErrorHandler` and then rejects the bootstrap with the
 * same value, which `startApp` reports. Errors the `ErrorHandler` receives while the app is starting are reported as
 * `Handler: Bootstrap` and remembered until the start finishes, so `startApp` skips the second report and a failed
 * start stays one crash in one group, whichever path caught it. Failures before the `ErrorHandler` exists reach
 * `startApp` only. An error in the first render, which runs before the bootstrap resolves, is also reported as
 * `Handler: Bootstrap`.
 *
 * A window error a rule proves is not ours is sent as a third-party warning instead of a crash. Those warnings have a
 * storm guard of their own, so they never spend the page's crash limit.
 */
export const createCrashReporter = (
	sdk: CrashReporterSdk,
	stormGuard: CrashStormGuard = createCrashStormGuard(),
	describePage: () => PageContext = () => pageContext(document, performance),
	classify: WindowErrorClassifier = (event) =>
		classifyWindowError(event, document.location.origin)
): CrashReporter => {
	const startup = { done: false }
	const thirdPartyGuard = createCrashStormGuard()
	const reportedWhileStarting = new Set<unknown>()

	/** Whether the storm guard lets one more crash of this kind through; the first one it stops becomes a warning. */
	const admitted = ({ title, error, handler }: Failure): boolean => {
		const verdict = stormGuard(crashStormKey(handler, title, error))

		if (verdict === CrashStormVerdict.Send) return true
		if (verdict !== CrashStormVerdict.Drop)
			sdk.warn(CRASH_STORM_WARNING, { limit: verdict, handler, title })

		return false
	}

	const sendCrash = ({ title, filename, line, error, handler }: Failure): void => {
		const crash = crashReport(title, filename, line, error, handler)
		// The SDK's own failure must not come back as an unhandled rejection, and from there as another crash.
		sdk.sendCrash(crash.title, crash.text).catch(() => undefined)
	}

	const send = (failure: Failure): void => {
		if (!admitted(failure)) return

		sendCrash(failure)
	}

	const sendAngular = (error: unknown, handler: CrashHandler): void =>
		send({ title: crashTitle(error, handler), error, handler })

	// The crash is already counted against the storm limits: failing to describe the page must not lose it.
	const warnMaskedScriptError = (): void => {
		try {
			sdk.warn(SCRIPT_ERROR_CONTEXT_WARNING, describePage())
		} catch {
			return
		}
	}

	// A rule that fails to decide leaves the error a crash.
	const ruleFor = (event: ErrorEvent): CrashRule | undefined => {
		try {
			return classify(event)
		} catch {
			return undefined
		}
	}

	const warnThirdParty = (
		failure: Failure,
		rule: CrashRule,
		verdict: CrashStormVerdict
	): void => {
		const { title, filename, line, error, handler } = failure

		if (verdict !== CrashStormVerdict.Send)
			return sdk.warn(THIRD_PARTY_STORM_WARNING, { limit: verdict, rule, title })

		sdk.warn(THIRD_PARTY_ERROR_WARNING, {
			rule,
			body: crashReport(title, filename, line, error, handler).text,
		})
	}

	/**
	 * Sends the warning that replaces the crash. A session uploads its logs only when it has a crash, so the upload is
	 * forced once for each warning sent: otherwise the warning would stay on the device.
	 */
	const sendThirdParty = (failure: Failure, rule: CrashRule): void => {
		const verdict = thirdPartyGuard(
			crashStormKey(failure.handler, failure.title, failure.error)
		)

		if (verdict === CrashStormVerdict.Drop) return

		warnThirdParty(failure, rule, verdict)
		sdk.forceSendOnce()
	}

	return {
		reportAngularError: (error) => {
			if (startup.done) return sendAngular(error, CrashHandler.Angular)

			reportedWhileStarting.add(error)
			sendAngular(error, CrashHandler.Bootstrap)
		},
		reportFailedStart: (error) => {
			if (reportedWhileStarting.has(error)) return

			sendAngular(error, CrashHandler.Bootstrap)
		},
		markStarted: () => {
			startup.done = true
			reportedWhileStarting.clear()
		},
		// The SDK's own listener: a message that is not a string is not reported.
		reportWindowError: (event) => {
			const { message, filename, lineno, error } = event

			if (typeof message !== 'string') return

			const failure: Failure = {
				title: message,
				filename,
				line: lineno,
				error,
				handler: CrashHandler.Error,
			}
			const rule = ruleFor(event)

			if (rule) return sendThirdParty(failure, rule)
			if (!admitted(failure)) return
			if (isMaskedScriptError(event)) warnMaskedScriptError()

			sendCrash(failure)
		},
		// The SDK's own listener: an Error by its message, anything else under the SDK title.
		reportRejection: (reason) => {
			const title = reason instanceof Error ? reason.message : UNHANDLED_REJECTION_TITLE

			if (typeof title !== 'string') return

			send({ title, error: reason, handler: CrashHandler.UnhandledRejection })
		},
	}
}

/** The page's one crash reporter, shared by the window listeners, `startApp` and the `ErrorHandler`. */
export const crashReporter = createCrashReporter(Bugfender)

/** Hands the `ErrorHandler` the page's one reporter, the same one `startApp` marks as started. */
export const CRASH_REPORTER = new InjectionToken<CrashReporter>('CRASH_REPORTER', {
	providedIn: 'root',
	factory: () => crashReporter,
})
